import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import authRequest from "@/api/auth-request";
import { SubmitButton } from "@/components/ui/SubmitButton";
import { cn } from "@/utils/cn";

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email"),
});

type FormData = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loading = isSubmitting;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      const response = await authRequest.request.resetUserPassword({
        email: data.email,
      });
      if (!response.success) {
        toast.error(
          "Sending email link for reset password failed. Please try again."
        );
        return;
      }
      toast.success(
        "Check your email for the reset password link."
      );
      navigate("/account/login");
    } catch {
      toast.error(
        "Something went wrong while sending the password reset email."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="card card-featured mb-4">
      <div className="card-body">
        <span>
          <h3 className="label">Request new password</h3>
        </span>
        <p className="mb-4 text-sm" style={{ color: "#6F7375" }}>
          If an email is connected to your account, we will send a link to reset
          your password.
        </p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label htmlFor="forgot-pwd-email" className="label f-weight-550">
              Email
            </label>
            <input
              id="forgot-pwd-email"
              type="email"
              placeholder="Enter email"
              autoComplete="email"
              className={cn("form-control form-control-sm", errors.email && "border-red-500")}
              {...register("email")}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
            )}
          </div>
          <div className="form-group mb-4 flex flex-wrap items-center gap-3">
            <SubmitButton
              isSubmitting={loading}
              loadingText="Sending…"
              className="btn btn-primary"
            >
              Send reset link
            </SubmitButton>
            <Link to="/account/login" className="hs-auth-link">Return to login</Link>
          </div>
        </form>
      </div>
    </section>
  );
}
